import { faPlus, faWarning } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useKeycloak } from "@react-keycloak/web";
import { Button, type DataTableFilter, FilterableDataTable } from "@ventumsoftware/react-toolkit";
import moment from "moment/moment";
import { type FC, useCallback, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useApi } from "../api/APIContext";
import type { Company, Exam, ExamParticipant, Project } from "../api/dto";
import type { Inspection } from "../api/dto/Inspection";
import type { QuestionSet } from "../api/dto/QuestionSet";
import { ContentContainer } from "../components/content/ContentContainer";
import { PageHeader } from "../components/content/PageHeader";
import { AssessmentStatusBadge } from "../components/data/AssessmentStatusBadge";
import { AssessmentTypeBadge } from "../components/data/AssessmentTypeBadge";
import { useModal } from "../components/layout/ModalProvider";
import { AddExamModal } from "../modals/AddExamModal";
import { AddInspectionModal } from "../modals/AddInspectionModal";

type Assessment = (Inspection & { type: "inspection" }) | (Exam & { type: "exam" });
type EnrichedAssessment = Assessment & {
  projectName: string;
  project: Project | undefined;
  companyName: string;
  company: Company | undefined;
  questionSet: QuestionSet | undefined;
  activeParticipants: number;
  /** questionSetType is the question set's type (e.g. "Fct"), or "" when unknown, so it can be filtered on. */
  questionSetType: string;
  /** lastUpdatedSortKey is an ISO timestamp, so the "Laatste wijziging" column sorts chronologically. */
  lastUpdatedSortKey: string;
};

const statusOptions = {
  "": "Alle",
  Afgerond: "Afgerond",
  "In uitvoering": "In uitvoering",
  Concept: "Concept",
  Gearchiveerd: "Gearchiveerd",
};

export const OverviewPage: FC = () => {
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();
  const roles = useMemo(
    () => keycloak.tokenParsed?.realm_access?.roles ?? [],
    [keycloak.tokenParsed],
  );
  const isCustomer = roles.includes("customer");
  const [searchParams, setSearchParams] = useSearchParams();

  if (isCustomer) {
    navigate("/browse");
  }

  const { exams, inspections, projects, companies, questionSets, participants, loading } = useApi();

  // The filters of the table are stored in the url by the FilterableDataTable.
  const filterCompany = searchParams.get("companyId") ?? "";
  const filterProject = searchParams.get("projectId") ?? "";

  const updateSearchParams = useCallback(
    (update: (params: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          update(next);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const projectsMap = useMemo(() => {
    return projects.reduce(
      (map, project) => {
        map[project.id] = project;
        return map;
      },
      {} as { [key: string]: Project },
    );
  }, [projects]);

  const companiesMap = useMemo(() => {
    return companies.reduce(
      (map, company) => {
        map[company.id] = company;
        return map;
      },
      {} as { [key: string]: Company },
    );
  }, [companies]);

  const questionSetsMap = questionSets.reduce(
    (map, questionSet) => {
      map[questionSet.id] = questionSet;
      return map;
    },
    {} as { [key: string]: QuestionSet },
  );

  const participantStatisticsMap = useMemo(() => {
    const participantMap = participants.reduce(
      (map, participant) => {
        if (map[participant.examId] === undefined) {
          map[participant.examId] = [];
        }
        map[participant.examId].push(participant);
        return map;
      },
      {} as { [key: string]: ExamParticipant[] },
    );
    return Object.fromEntries(
      Object.entries(participantMap).map(([id, participants]) => {
        return [
          id,
          {
            participants: participants.length,
            noShows: participants.filter((p) => p.noShow).length,
            activeParticipants: participants.filter((p) => !p.noShow).length,
          },
        ];
      }),
    );
  }, [participants]);

  const enrichedExams = useMemo(() => {
    const assessments: Assessment[] = [
      ...exams.map((e) => ({ ...e, type: "exam" as "exam" })),
      ...inspections.map((i) => ({ ...i, type: "inspection" as "inspection" })),
    ];
    return assessments.map((exam) => {
      const project = projectsMap[exam.projectId];
      const company = companiesMap[project?.companyId ?? ""];
      const questionSet = questionSetsMap[exam.questionSetId];
      const examStats = participantStatisticsMap[exam.id];
      return {
        ...exam,
        projectName: project?.name ?? "Onbekend project",
        project,
        companyName: company?.name ?? "Onbekend bedrijf",
        company,
        questionSet,
        activeParticipants: examStats?.activeParticipants ?? 0,
        questionSetType: questionSet?.type ?? "",
        lastUpdatedSortKey: exam.lastUpdated.toISOString(),
      };
    });
  }, [exams, inspections, projectsMap, companiesMap, questionSetsMap, participantStatisticsMap]);

  // Newest first by default, the user can sort on the column headers.
  const tableData = useMemo(
    () => [...enrichedExams].sort((a, b) => (b.name ?? "").localeCompare(a.name ?? "")),
    [enrichedExams],
  );
  const companyOptions = useMemo(() => {
    const options = Object.entries(companiesMap)
      .filter(([, company]) => !company.isArchived)
      .map(([id, company]) => [id, company.name]);
    return Object.fromEntries([["", "Alle"], ...options]);
  }, [companiesMap]);

  const projectOptions = useMemo(() => {
    const options = Object.entries(projectsMap)
      .filter(([, project]) => !project.isArchived)
      .filter(([, project]) => filterCompany === "" || project.companyId === filterCompany)
      .map(([id, project]) => [
        id,
        filterCompany === ""
          ? `${project.name} (${companiesMap[project.companyId]?.name ?? "-"})`
          : project.name,
      ]);
    return Object.fromEntries([["", "Alle"], ...options]);
  }, [projectsMap, companiesMap, filterCompany]);

  const questionSetTypeOptions = useMemo(() => {
    const types = [...new Set(questionSets.map((questionSet) => questionSet.type))].sort();
    return Object.fromEntries([["", "Alle"], ...types.map((type) => [type, type])]);
  }, [questionSets]);

  // Drop a project selection that doesn't belong to the selected company (anymore).
  useEffect(() => {
    if (filterProject === "" || filterCompany === "") return;
    if (projectsMap[filterProject]?.companyId !== filterCompany) {
      updateSearchParams((params) => params.delete("projectId"));
    }
  }, [filterCompany, filterProject, projectsMap, updateSearchParams]);

  const filters = useMemo<DataTableFilter<EnrichedAssessment>[]>(
    () => [
      {
        label: "Type",
        property: "type",
        options: { "": "Alle", exam: "Competency Test", inspection: "Inspectie" },
      },
      { label: "Bedrijf", property: "companyId", options: companyOptions },
      { label: "Project", property: "projectId", options: projectOptions },
      { label: "Vragenlijst", property: "questionSetType", options: questionSetTypeOptions },
      {
        label: "Status",
        property: "status",
        options: statusOptions,
        // "Alle" hides archived assessments, they can be shown by selecting "Gearchiveerd".
        predicate: (row, value) => (value === "" ? row.archivedAt === null : row.status === value),
      },
    ],
    [companyOptions, projectOptions, questionSetTypeOptions],
  );

  /** showFinishedFirOn shows the finished Field Inspection Reports of the given day. */
  const showFinishedFirOn = (day: moment.Moment) => {
    updateSearchParams((params) => {
      params.set("search", day.format("YYYY-MM-DD"));
      params.set("questionSetType", "Fir");
      params.set("status", "Afgerond");
      params.delete("page");
    });
  };

  // Stats
  const inspectionsToday = useMemo(() => {
    return enrichedExams
      .filter((e) => e.finishedAt !== null)
      .filter((e) => e.questionSet?.type === "Fir")
      .filter((e) => e.type === "exam" && moment(e.examAt).isSame(moment(), "day")).length;
  }, [enrichedExams]);
  const inspectionsYesterday = useMemo(() => {
    return enrichedExams
      .filter((e) => e.finishedAt !== null)
      .filter((e) => e.questionSet?.type === "Fir")
      .filter(
        (e) => e.type === "exam" && moment(e.examAt).isSame(moment().subtract(1, "day"), "day"),
      ).length;
  }, [enrichedExams]);

  const addInspectionModal = useModal({
    title: "Inspectie toevoegen",
    body: (
      <AddInspectionModal
        company={companies.find((c) => c.id === filterCompany)}
        project={projects.find((p) => p.id === filterProject)}
      />
    ),
  });

  const addExamModal = useModal({
    title: "Competency test toevoegen",
    body: (
      <AddExamModal
        company={companies.find((c) => c.id === filterCompany)}
        project={projects.find((p) => p.id === filterProject)}
      />
    ),
  });

  return (
    <ContentContainer size={"xl"}>
      <div className={"flex items-center justify-between"}>
        <PageHeader>Overzicht</PageHeader>
        <div className={"flex items-center gap-2"}>
          <Button
            type={"primary"}
            size={"md"}
            icon={faPlus}
            text={"Competency test toevoegen"}
            onClick={() => addExamModal.open()}
          />
          <Button
            type={"primary"}
            size={"md"}
            icon={faPlus}
            text={"Inspectie toevoegen"}
            onClick={() => addInspectionModal.open()}
          />
        </div>
      </div>

      <div className={"grid grid-cols-1 lg:grid-cols-5 gap-4 mb-6"}>
        <div>
          <div className={"mb-1 font-medium text-slate-800"}>Inspections</div>
          <div className={"border border-slate-200 bg-white rounded-sm "}>
            <button
              className={
                "w-full px-3 py-3 flex flex-col items-center rounded-sm justify-center hover:bg-brand-50"
              }
              onClick={() => showFinishedFirOn(moment())}
            >
              <div className={"text-sm"}>Vandaag afgerond</div>
              <div className={"text-lg font-bold mt-1"}>{inspectionsToday}</div>
            </button>
          </div>
        </div>
        <div>
          <div className={"mb-1 font-medium text-slate-800"}>&nbsp;</div>
          <div className={"border border-slate-200 bg-white rounded-sm "}>
            <button
              className={
                "w-full px-3 py-3 flex flex-col items-center rounded-sm justify-center hover:bg-brand-50"
              }
              onClick={() => showFinishedFirOn(moment().subtract(1, "day"))}
            >
              <div className={"text-sm"}>Gisteren afgerond</div>
              <div className={"text-lg font-bold mt-1"}>{inspectionsYesterday}</div>
            </button>
          </div>
        </div>
      </div>

      <FilterableDataTable<EnrichedAssessment>
        data={tableData}
        keyProperty={"id"}
        loading={loading}
        pageSize={50}
        searchableProperties={["name", "projectName", "companyName"]}
        searchPlaceholder={"Zoek op naam, project of bedrijf"}
        filters={filters}
        columns={[
          { header: "Naam", sort: true, property: "name" },
          {
            header: "Type",
            sort: true,
            property: "type",
            transform: (type: EnrichedAssessment["type"]) => <AssessmentTypeBadge type={type} />,
          },
          {
            header: "Project",
            property: "projectId",
            transform: (projectId: EnrichedAssessment["projectId"]) => {
              const project = projects.find((project) => project.id === projectId);
              if (!project)
                return (
                  <div className={"text-red-600 font-medium flex items-center"}>
                    <FontAwesomeIcon icon={faWarning} className={"mr-1"} />
                    <span>Geen</span>
                  </div>
                );
              if (project.isArchived)
                return (
                  <div className={"text-amber-600 font-medium flex items-center"}>
                    <FontAwesomeIcon icon={faWarning} className={"mr-1"} />
                    <span>{project.name}</span>
                  </div>
                );
              return project.name;
            },
          },
          {
            header: "Bedrijf",
            property: "companyId",
            transform: (companyId: EnrichedAssessment["companyId"]) => {
              const company = companies.find((company) => company.id === companyId);
              if (!company)
                return (
                  <div className={"text-red-600 font-medium flex items-center"}>
                    <FontAwesomeIcon icon={faWarning} className={"mr-1"} />
                    <span>Geen</span>
                  </div>
                );
              if (company.isArchived)
                return (
                  <div className={"text-amber-600 font-medium flex items-center"}>
                    <FontAwesomeIcon icon={faWarning} className={"mr-1"} />
                    <span>{company.name}</span>
                  </div>
                );
              return company.name;
            },
          },
          {
            header: "Status",
            sort: true,
            property: "status",
            transform: (status: EnrichedAssessment["status"]) => (
              <AssessmentStatusBadge status={status} />
            ),
          },
          {
            header: "Deelnemers",
            property: "activeParticipants",
            transform: (
              value: EnrichedAssessment["activeParticipants"],
              row: EnrichedAssessment,
            ) => (row.type === "exam" ? value : ""),
          },
          {
            header: "Laatste wijziging",
            sort: true,
            property: "lastUpdatedSortKey",
            transform: (_: string, row: EnrichedAssessment) => (
              <span className={"whitespace-nowrap"}>{row.lastUpdated.toLocaleString("nl")}</span>
            ),
          },
        ]}
        onClickRow={(exam) => {
          navigate(`/${exam.type === "inspection" ? "inspections" : "exams"}/${exam.id}`);
        }}
      />
    </ContentContainer>
  );
};
